import React, { useState, useRef, useEffect } from "react";
import "../styles/BookingSettings.css";
import { MdOutlineNoteAlt } from "react-icons/md";
import { RiDeleteBin6Line } from "react-icons/ri";
import { TbEdit } from "react-icons/tb";
import API_URL from "../config";
import { useAdmin } from "../contexts/AdminContext";

const BookingSettings = () => {
  const { admin } = useAdmin();
  const days = [
    "AllDays (Mon-Sat)",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
  ];
  const timeSlots = Array.from({ length: 73 }, (_, i) => {
    const hours24 = Math.floor(i / 4) + 6; // Divide by 4 instead of 2 for 15-minute intervals
    const minutes = (i % 4) * 15; // Multiply by 15 to get 15-minute intervals
    const period = hours24 >= 12 ? "PM" : "AM";
    const hours12 = hours24 > 12 ? hours24 - 12 : hours24 === 12 ? 12 : hours24;
    return `${hours12}:${minutes.toString().padStart(2, "0")} ${period}`;
  });
  const updateSlot = (field, value) => {
    setSlot({ ...slot, [field]: value });
  };

  const [slot, setSlot] = useState({ day: "", from: "", to: "", slots: "" });
  const [savedSlots, setSavedSlots] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);

  const handleSave = async () => {
    try {
      const { day, from, to, slots } = slot;

      if (!day || !from || !to || !slots) {
        alert("All fields are required");
        return;
      }

      const slotData = {
        time: `${from} - ${to}`,
        day: day,
        limit: parseInt(slots, 10),
        branch: admin?.branch,
      };

      let response;
      if (editMode && selectedSlot) {
        response = await fetch(`${API_URL}/slot/${selectedSlot._id}`, {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(slotData),
        });
      } else {
        response = await fetch(`${API_URL}/slot`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(slotData),
        });
      }

      if (!response.ok) {
        throw new Error("Failed to save slot");
      }

      const data = await response.json();
      fetchSlots();

      setSlot({ day: "", from: "", to: "", slots: "" });
      setEditMode(false);
      setSelectedSlot(null);
    } catch (error) {
      console.error("Error saving slot:", error);
    }
  };

  const handleCancel = () => {
    setSlot({ day: "", from: "", to: "", slots: "" });
    setEditMode(false);
    setSelectedSlot(null);
  };

  const fetchSlots = async () => {
    try {
      const response = await fetch(`${API_URL}/slot/${admin?.branch}`);
      if (!response.ok) {
        throw new Error("Failed to fetch slots");
      }
      const data = await response.json();
      setSavedSlots(data);
    } catch (error) {
      console.error("Error fetching slots:", error);
    }
  };

  useEffect(() => {
    fetchSlots();
  }, []);

  const handleDelete = async (slotId) => {
    try {
      const response = await fetch(`${API_URL}/slot/${slotId}`, {
        method: "DELETE",
      });

      if (!response.ok) {
        throw new Error("Failed to delete slot.");
      }

      const data = await response.json();
      alert("Slot deleted successfully!");
      fetchSlots();
    } catch (error) {
      console.error("An error occurred while deleting the slot.");
    }
  };

  const handleEdit = (slot) => {
    const [from, to] = slot.time.split(" - ");
    setSlot({
      day: slot?.day,
      from: from,
      to: to,
      slots: slot.limit.toString(),
    });
    setEditMode(true);
    setSelectedSlot(slot);
  };

  // Custom Dropdown Component
  const CustomDropdown = ({ options, value, onChange }) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);

    const handleOptionClick = (option) => {
      onChange(option);
      setIsOpen(false);
    };

    useEffect(() => {
      const handleClickOutside = (event) => {
        if (
          dropdownRef.current &&
          !dropdownRef.current.contains(event.target)
        ) {
          setIsOpen(false);
        }
      };

      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, [dropdownRef]);

    return (
      <div className="custom-dropdown" ref={dropdownRef}>
        <button
          type="button"
          className="dropdown-toggle"
          onClick={() => setIsOpen(!isOpen)}
        >
          {value || "Select"}
        </button>
        {isOpen && (
          <ul className="dropdown-options">
            {options.map((option) => (
              <li key={option} onClick={() => handleOptionClick(option)}>
                {option}
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  };

  const dayOrder = {
    Monday: 1,
    Tuesday: 2,
    Wednesday: 3,
    Thursday: 4,
    Friday: 5,
    Saturday: 6,
    Sunday: 7,
    "All Days": 0,
  };

  const sortedSlots = [...savedSlots].sort((a, b) => {
    return dayOrder[a.day] - dayOrder[b.day];
  });

  return (
    <div className="membershipManagementContainer">
      <div className="userManagementHeader">
        <div className="userHeaderLeft">
          <MdOutlineNoteAlt
            size={30}
            color="#ff0000"
            style={{ marginRight: 10 }}
          />
          <h2>Booking Settings</h2>
        </div>
      </div>
      <h3>Booking Schedule:</h3>
      <div style={{ marginTop: 20, marginBottom: 30 }}>
        <div className="slot-row">
          <label style={{ display: "flex", flexDirection: "column" }}>
            Select Day
            <CustomDropdown
              options={days}
              value={slot.day}
              onChange={(value) => updateSlot("day", value)}
            />
          </label>

          <label style={{ display: "flex", flexDirection: "column" }}>
            From
            <CustomDropdown
              options={timeSlots}
              value={slot.from}
              onChange={(value) => updateSlot("from", value)}
            />
          </label>

          <label style={{ display: "flex", flexDirection: "column" }}>
            To
            <CustomDropdown
              options={timeSlots.slice(timeSlots.indexOf(slot.from) + 1)}
              value={slot.to}
              onChange={(value) => updateSlot("to", value)}
            />
          </label>

          <label style={{ display: "flex", flexDirection: "column" }}>
            Slots
            <input
              type="string"
              placeholder="eg. 100"
              value={slot.slots}
              onChange={(e) => updateSlot("slots", e.target.value)}
              style={{
                marginTop: 10,
                height: 35,
                borderRadius: 4,
                width: "150px",
              }}
            />
          </label>
        </div>

        <div className="saveCancelBtnContainer">
          <button className="saveBookingSlot" onClick={handleSave}>
            {editMode ? "Update" : "Save"}
          </button>
          <button className="cancelBookingSlot" onClick={handleCancel}>
            Cancel
          </button>
        </div>
      </div>
      <h3>Booking Details:</h3>
      <div className="table-container">
        <table className="custom-table">
          <thead>
            <tr>
              <th>Day</th>
              <th>Time</th>
              <th>Slots</th>
              <th>Edit</th>
              <th>Delete</th>
            </tr>
          </thead>
          <tbody>
            {sortedSlots.map((savedSlot, index) => (
              <tr key={index}>
                <td>{savedSlot.day}</td>
                <td>{savedSlot.time}</td>
                <td>{savedSlot.limit}</td>
                <td className="editBtnGymTable">
                  <TbEdit
                    style={{ cursor: "pointer" }}
                    onClick={() => handleEdit(savedSlot)}
                  />
                </td>
                <td
                  className="deleteBtnGymTable"
                  onClick={() => handleDelete(savedSlot._id)}
                >
                  <RiDeleteBin6Line style={{ cursor: "pointer" }} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default BookingSettings;
